import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  Flex,
  Grid,
  ScrollArea,
  SegmentedControl,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  IconCircleCheck,
  IconCircleDot,
  IconCircleX,
  IconDotsCircleHorizontal,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import QueryString from "qs";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import CardHeader from "@/components/CardHeader";
import ExcelDownload from "@/components/ExcelDownload";
import OrderImagesViewer from "@/components/OrderImagesViewer";
import OrderDetail from "@/components/OrderDetail";
import Empty from "@/components/placeholders/Empty";
import TableEmpty from "@/components/placeholders/TableEmpty";
import RoutePagination from "@/components/RoutePagination";
import useAuthStore from "@/store/useAuthStore";
import { FEE_ORDER_STATUS, ORDER_STATUS } from "@/utils/constants";
import { parseMoney, parsePhone } from "@/utils/helpers";

export default function History() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = useAuthStore((state) => state.pageSize);

  const page = searchParams.get("page") || 1;
  const activeTab = searchParams.get("activeTab") || "all";
  const user = useAuthStore((state) => state.user);
  const isUser98 = user?.id === 98;

  const form = useForm({
    initialValues: {
      startName: "",
      endName: "",
      startDate: null,
      endDate: null,
    },
  });

  const [sort, setSort] = useState("startDate:desc,updatedAt:desc");
  const [filters, setFilters] = useState({});

  function getStatus(activeTab) {
    if (activeTab === "all")
      return ["requested", "accounting", "withdraw", "finished", "feeRequested", "feeFinished"];
    if (activeTab === "requested") return ["requested", "feeRequested"];
    if (activeTab === "done") return ["accounting", "withdraw", "finished", "feeFinished"];
    if (activeTab === "canceled") return ["canceled"];
  }

  const EXTENDED_ORDER_STATUS = [
    ...ORDER_STATUS,
    ...FEE_ORDER_STATUS,
    {
      value: "register",
      label: "접수",
      color: "cyan",
    },
    {
      value: "feeRegister",
      label: "접수",
      color: "cyan",
    },
  ];

  function getExtendedStatus(status, createdAt) {
    // status가 requested 이고 createdAt 이 지금으로부터 3분 이내이면 register
    if (status === "requested" && dayjs(createdAt).isAfter(dayjs().subtract(3, "minute"))) {
      return "register";
    }

    if (status === "feeRequested" && dayjs(createdAt).isAfter(dayjs().subtract(3, "minute"))) {
      return "feeRegister";
    }

    if (status === "feeRequested") {
      return "feeRequested";
    }

    if (status === "feeFinished") {
      return "feeFinished";
    }

    if (status === "requested") {
      return "requested";
    }

    if (status === "canceled") {
      return "canceled";
    }

    return "accounting";
  }

  const status = getStatus(activeTab);

  const params = QueryString.stringify({
    filters: {
      status: {
        $in: status,
      },
      startDate: {
        // only this month
        // $gte: dayjs().startOf("month").format("YYYY-MM-DD"),
        // $lte: dayjs().endOf("month").format("YYYY-MM-DD"),
      },
      ...filters,
      company: user?.company?.id,
    },
    populate: "*",
    sort: [sort],
    pagination: {
      page,
      pageSize,
    },
  });

  const title = `배차내역`;
  const { data, isLoading, mutate } = useSWR(["/orders", params], {
    refreshInterval: 15000,
  });

  function handleSearch() {
    setSearchParams((prev) => {
      prev.set("page", 1);
      return prev;
    });
    let newFilter = {};

    // EDIT FILTERS
    const { startName, endName, startDate, endDate } = form.values;
    if (startName) newFilter = { ...newFilter, startName: { $contains: startName } };
    if (endName) newFilter = { ...newFilter, endName: { $contains: endName } };
    newFilter = {
      ...newFilter,
      startDate: {
        $gte: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
        $lte: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
      },
    };

    setFilters(newFilter);
  }

  function handleClearSearch() {
    setSearchParams((prev) => {
      prev.set("page", 1);
      return prev;
    });
    form.reset();
    setFilters({});
  }

  if (!user?.company?.id)
    return (
      <Empty
        title="Error"
        text={`회사 정보를 찾을 수 없습니다. ${JSON.stringify(user?.company?.id)}`}
      />
    );

  return (
    <Container fluid>
      <Card>
        <CardHeader
          title={`${title}`}
          action={
            <SegmentedControl
              value={activeTab}
              onChange={(x) => {
                setSearchParams((prev) => {
                  prev.set("activeTab", x);
                  prev.set("page", 1);
                  return prev;
                });
              }}
              styles={{ labelActive: { fontWeight: 700 } }}
              data={[
                {
                  value: "all",
                  label: (
                    <Center>
                      <IconCircleDot size="1rem" />
                      <Box ml={4}>전체</Box>
                    </Center>
                  ),
                },
                {
                  value: "requested",
                  label: (
                    <Center>
                      <IconDotsCircleHorizontal size="1rem" />
                      <Box ml={4}>배차중</Box>
                    </Center>
                  ),
                },
                {
                  value: "done",
                  label: (
                    <Center>
                      <IconCircleCheck size="1rem" />
                      <Box ml={4}>배차완료</Box>
                    </Center>
                  ),
                },
                {
                  value: "canceled",
                  label: (
                    <Center>
                      <IconCircleX size="1rem" />
                      <Box ml={4}>취소</Box>
                    </Center>
                  ),
                },
              ]}
            />
          }
        />

        <Card.Section withBorder>
          <Box my="sm" mx="md">
            <form onSubmit={form.onSubmit(handleSearch)}>
              <Grid style={{ flexWrap: "wrap" }}>
                <Grid.Col span={6} md={2}>
                  <TextInput mb={0} placeholder="상차지명" {...form.getInputProps("startName")} />
                </Grid.Col>

                <Grid.Col span={6} md={2}>
                  <TextInput mb={0} placeholder="하차지명" {...form.getInputProps("endName")} />
                </Grid.Col>

                <Grid.Col span={6} md={2}>
                  <DatePickerInput
                    placeholder="상차 시작일"
                    {...form.getInputProps("startDate")}
                    clearable
                  />
                </Grid.Col>

                <Grid.Col span={6} md={2}>
                  <DatePickerInput
                    placeholder="상차 종료일"
                    {...form.getInputProps("endDate")}
                    clearable
                  />
                </Grid.Col>

                <Grid.Col span={12} md={2}>
                  <Flex gap="xs">
                    <Button variant="outline" onClick={handleClearSearch} sx={{ flex: 1 }}>
                      초기화
                    </Button>
                    <Button type="submit" sx={{ flex: 1 }}>
                      검색
                    </Button>
                  </Flex>
                </Grid.Col>

                {!isUser98 && (
                  <Grid.Col span={12} md={2}>
                    <ExcelDownload params={params} />
                  </Grid.Col>
                )}
              </Grid>
            </form>
          </Box>
        </Card.Section>

        <Card.Section>
          <ScrollArea>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th className="tc">상태</th>
                  <th className="tc">상차일</th>
                  <th className="tl">상차지</th>
                  <th className="tl">하차지</th>
                  <th className="tc">차량정보</th>
                  <th className="tc">차주 연락처</th>
                  <th className="tc">옵션</th>
                  {!isUser98 && <th className="tr">공급가액/부가세</th>}
                  {!isUser98 && <th className="tr">합계</th>}
                  <th className="tc">등록일</th>
                  <th className="tc">관리</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && data?.data?.length === 0 && (
                  <TableEmpty
                    title="배차 내역이 없습니다."
                    text="신규 배차 신청에서 배차를 요청해보세요!"
                  />
                )}
                {data?.data?.map((row, idx) => {
                  const item = row.attributes || {};

                  const _status = getExtendedStatus(item.status, item.createdAt);

                  const statusObj = EXTENDED_ORDER_STATUS.find((s) => s.value === _status);

                  return (
                    <tr key={row.id}>
                      <td>{idx + (page - 1) * pageSize + 1}</td>
                      <td className="tc">
                        <Badge color={statusObj?.color} variant="filled" radius="sm">
                          {statusObj?.label}
                        </Badge>
                      </td>
                      <td className="tc">
                        <Box>
                          <Text size="sm">{dayjs(item.startDate).format("MM/DD")}</Text>

                          {item.isFeeOrder && (
                            <Badge color="cyan" variant="filled" radius="sm">
                              선착불
                            </Badge>
                          )}
                        </Box>
                      </td>
                      <td className="tl">
                        <b>{item.startName}</b>
                        <br />
                        <Text size="xs">{item.startAddress}</Text>
                      </td>
                      <td className="tl">
                        <b>{item.endName}</b>
                        <br />
                        <Text size="xs">{item.endAddress}</Text>
                      </td>

                      <td className="tc">
                        {item?.driverCarNumber ? (
                          <>
                            {item.driverCarInfo}
                            <br />
                            {item.driverCarNumber}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="tc">{parsePhone(item.driverPhoneNumber)}</td>
                      <td className="tc">
                        {item.isRoundTrip && (
                          <Badge color="grape" variant="filled" size="md" px={4}>
                            왕복
                          </Badge>
                        )}
                        {item.isMixed && (
                          <Badge color="violet" variant="filled" size="md" px={4}>
                            혼적
                          </Badge>
                        )}
                      </td>
                      {!isUser98 && (
                        <td className="tr">
                          {item?.price ? (
                            <>
                              {`${parseMoney(item.price)}원`}
                              <br />
                              <Text size="xs">{`${parseMoney(item.price * 0.1)}원`}</Text>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      {!isUser98 && (
                        <td className="tr">
                          <b>{item.price ? `${parseMoney(item.price * 1.1)}원` : "-"}</b>
                        </td>
                      )}

                      <td className="tc">
                        {dayjs(item.createdAt).format("YY/MM/DD")} <br />
                        {dayjs(item.createdAt).format("HH:mm")}
                      </td>
                      <td className="tc">
                        <Flex gap="xs" justify="center">
                          <OrderImagesViewer
                            receiptImage={item.receiptImage}
                            orderImages={item.orderImages}
                            label="인수증"
                            compact
                          />
                          <OrderDetail id={row.id} mutate={mutate} />
                        </Flex>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </ScrollArea>
        </Card.Section>

        <RoutePagination
          total={data?.meta?.pagination?.pageCount}
          page={page}
          setSearchParams={setSearchParams}
        />
      </Card>
    </Container>
  );
}
